import { ColumnDef } from "@tanstack/react-table";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
import {
  EXPORT_TYPES,
  FR_ACCESS_STATUSES,
  FR_IDENTITY_TYPES,
  REPORT_NAMES,
  TABLE_IDS,
  tableColumnsAr,
  tableColumnsEn,
} from "../../../constants/common";
import {
  downloadFile,
  formattedDateTimeWithoutSec,
} from "../../../lib/functions";
import {
  FRAccessLog,
  ExportFRAccessLogsReportForExcelPDFBody,
} from "../../../hooks/report/interface";
import { cn } from "../../../utils/cn";
import ViewAttachmentModal from "../../modal/ViewAttachmentModal";
import {
  useExportFRAccessLogsReportForExcel,
  useExportFRAccessLogsReportForPDF,
} from "../../../hooks/report/mutation";
import StatusCodes from "../../../constants/StatusCodes";

interface AccessLogReportTableProps {
  allLogs: FRAccessLog[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  exportBody?: ExportFRAccessLogsReportForExcelPDFBody;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const AccessLogReportTable = ({
  allLogs,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  exportBody,
  pageSize,
  setPageSize,
}: AccessLogReportTableProps) => {
  const { i18n, t } = useTranslation();

  const [imageSrc, setImageSrc] = useState("");
  const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
    useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);

  const onPhotoClick = useCallback((attachment: string) => {
    setIsViewAttachmentModalOpen(true);
    setImageSrc(attachment);
  }, []);

  const { mutateAsync: ExportFRAccessLogsReportForExcel } =
    useExportFRAccessLogsReportForExcel();
  const { mutateAsync: ExportFRAccessLogsReportForPDF } =
    useExportFRAccessLogsReportForPDF();

  const logsColumns: ColumnDef<FRAccessLog>[] = useMemo(
    () => [
      {
        accessorKey: "s_Photo",
        header: () => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
          >
            {t("photo")}
          </Button>
        ),
        cell: ({ row }) => {
          const attachment = `data:image/png;base64,${row.original.s_Photo}`;
          return (
            <div
              onClick={() => onPhotoClick(attachment)}
              className="w-20 rounded-md overflow-hidden cursor-pointer"
            >
              <img src={attachment} alt="attachment" />
            </div>
          );
        },
      },
      {
        accessorKey: "s_Name",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("fullName")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_Name}</span>;
        },
      },
      {
        accessorKey: "s_Identity",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("identity")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          if (!row.original.n_IdentityType) return <></>;

          return (
            <div
              className={cn(
                "text-center rounded-lg py-0.5 px-1",
                row.original.n_IdentityType === FR_IDENTITY_TYPES.VISITOR
                  ? "bg-green-600"
                  : "bg-red-500"
              )}
            >
              <p className="text-white">
                {i18n.language === "en"
                  ? row.original.s_IdentityType_En
                  : row.original.s_IdentityType_Ar}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("location")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_LocationName_En
                : row.original.s_LocationName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_BuildingName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("building")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_BuildingName_En
                : row.original.s_BuildingName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "dt_AccessTime",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("accessTime")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {formattedDateTimeWithoutSec(row.original.dt_AccessTime)}
            </span>
          );
        },
      },
      {
        accessorKey: "s_Device",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("device")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_DeviceName_En
                : row.original.s_DeviceName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "n_AccessStatus",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("status")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <div
              className={cn(
                "text-center rounded-lg py-0.5 px-1",
                row.original.n_AccessStatus === FR_ACCESS_STATUSES.AUTHORIZED
                  ? "bg-green-600"
                  : "bg-red-500"
              )}
            >
              <p className="text-white">
                {i18n.language === "en"
                  ? row.original.s_AccessStatus_En
                  : row.original.s_AccessStatus_Ar}
              </p>
            </div>
          );
        },
      },
    ],
    [i18n.language, onPhotoClick, t]
  );

  const onExport = useCallback(
    async (type: EXPORT_TYPES) => {
      if (!exportBody) return;

      if (visibleColumnKeys.length === 0) {
        toast.error(t("noColumnSelectedForExport"));
        return;
      }

      try {
        const body = {
          ...exportBody,
          ExportOptions: visibleColumnKeys.map((key) => ({
            ColumnKey: key,
            ColumnNameEn: tableColumnsEn[key],
            ColumnNameAr: tableColumnsAr[key],
            CurrentLanguage: i18n.language,
          })),
        };

        let res;
        if (type === EXPORT_TYPES.EXCEL) {
          res = await ExportFRAccessLogsReportForExcel(body);
        } else {
          res = await ExportFRAccessLogsReportForPDF(body);
        }
        downloadFile(res, REPORT_NAMES.ACCESS_LOG_REPORT);
        toast.success(t("exportSuccess"));
      } catch (error: any) {
        if (error.statusCode === StatusCodes.NOT_FOUND) {
          toast.error(t("noDataToExport"));
        } else {
          toast.error(t("somethingWentWrong"));
        }
      }
    },
    [
      ExportFRAccessLogsReportForExcel,
      ExportFRAccessLogsReportForPDF,
      exportBody,
      i18n.language,
      t,
      visibleColumnKeys,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <TableWrapper
        data={allLogs}
        columns={logsColumns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isExportExcel
        onExport={onExport}
        isSearch
        tableId={TABLE_IDS.ACCESS_LOG_REPORT}
        setVisibleColumnKeys={setVisibleColumnKeys}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <ViewAttachmentModal
        attachment={imageSrc}
        contentType="image/png"
        isOpen={isViewAttachmentModalOpen}
        onOpenChange={() => {
          setIsViewAttachmentModalOpen(false);
          setImageSrc("");
        }}
      />
    </>
  );
};

export default memo(AccessLogReportTable);
